/**
 * QuestionFeedbackChip — 🚩 per-question flag on SubmissionReviewDetailPage
 *
 * Renders a small flag icon next to each question result row.
 * Tapping opens a mini-panel with preset tag chips (multi-select) + optional free text.
 * Auto-captures questionId, submissionId, assignmentId, AI marks, AI confidence.
 * Gated behind VITE_FEEDBACK_MODE env var — renders nothing when off.
 */

import { useState, useCallback } from 'react';
import {
  IconButton,
  Paper,
  Fade,
  Stack,
  Chip,
  TextField,
  Typography,
  Fab,
  Box,
  Tooltip,
} from '@mui/material';
import FlagIcon from '@mui/icons-material/Flag';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import { supabase } from '@/lib/supabase';

/** Preset tags from TUR-1539 spec */
const PRESET_TAGS = [
  { label: '評分不準', value: 'wrong_grade' },
  { label: 'OCR 錯誤', value: 'bad_ocr' },
  { label: '回饋無用', value: 'feedback_unhelpful' },
  { label: '離題判定錯誤', value: 'off_topic_misjudged' },
  { label: '其他', value: 'other' },
] as const;

interface QuestionFeedbackChipProps {
  questionId: string;
  submissionId: string;
  assignmentId: string;
  aiMarksAwarded?: number;
  aiConfidence?: number;
}

export function QuestionFeedbackChip({
  questionId,
  submissionId,
  assignmentId,
  aiMarksAwarded,
  aiConfidence,
}: QuestionFeedbackChipProps) {
  // Gate: render nothing when feedback mode is off
  if (import.meta.env.VITE_FEEDBACK_MODE !== 'true') return null;
  return (
    <QuestionFeedbackChipInner
      questionId={questionId}
      submissionId={submissionId}
      assignmentId={assignmentId}
      aiMarksAwarded={aiMarksAwarded}
      aiConfidence={aiConfidence}
    />
  );
}

function QuestionFeedbackChipInner({
  questionId,
  submissionId,
  assignmentId,
  aiMarksAwarded,
  aiConfidence,
}: QuestionFeedbackChipProps) {
  const [open, setOpen] = useState(false);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleToggleTag = useCallback((tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag],
    );
  }, []);

  const handleSubmit = useCallback(async () => {
    if (selectedTags.length === 0 && !comment.trim()) return;
    setSubmitting(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) {
        console.error('[QuestionFeedbackChip] No auth token');
        setSubmitting(false);
        return;
      }

      // Capture screenshot
      let screenshotData: string | null = null;
      try {
        const h2c = (await import('html2canvas')).default;
        const canvas = await h2c(document.body, {
          logging: false,
          useCORS: true,
          allowTaint: true,
          scale: 2,
        });
        screenshotData = canvas.toDataURL('image/jpeg', 0.85);
      } catch {
        // Non-critical
      }

      const res = await fetch('/api/feedback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          source: 'question_flag',
          sentiment: null,
          tags: selectedTags,
          comment: comment.trim() || null,
          page_route: window.location.pathname + window.location.search,
          assignment_id: assignmentId,
          submission_id: submissionId,
          question_id: questionId,
          ai_marks_awarded: aiMarksAwarded ?? null,
          ai_confidence: aiConfidence ?? null,
          screenshot_data: screenshotData,
        }),
      });

      if (!res.ok) {
        console.error('[QuestionFeedbackChip] Submit failed:', await res.text());
      } else {
        setSubmitted(true);
        setTimeout(() => {
          setOpen(false);
          setSubmitted(false);
          setSelectedTags([]);
          setComment('');
        }, 1200);
      }
    } catch (err) {
      console.error('[QuestionFeedbackChip] Error:', err);
    } finally {
      setSubmitting(false);
    }
  }, [selectedTags, comment, questionId, submissionId, assignmentId, aiMarksAwarded, aiConfidence]);

  if (submitted) {
    return (
      <Chip
        icon={<FlagIcon />}
        label="已標記"
        size="small"
        color="info"
        variant="outlined"
        sx={{ ml: 1 }}
      />
    );
  }

  return (
    <Box sx={{ display: 'inline-flex', position: 'relative', ml: 1 }}>
      <Tooltip title="標記此題問題" placement="top">
        <IconButton
          size="small"
          onClick={() => setOpen(!open)}
          sx={{
            color: open ? 'error.main' : 'action.default',
            '&:hover': { color: 'error.main' },
          }}
        >
          <FlagIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Fade in={open}>
        <Paper
          elevation={4}
          sx={{
            position: 'absolute',
            top: '100%',
            right: 0,
            mt: 0.5,
            p: 1.5,
            width: 260,
            zIndex: (theme) => theme.zIndex.tooltip,
          }}
        >
          <Stack spacing={1}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="caption" fontWeight={700}>
                🚩 標記問題
              </Typography>
              <IconButton size="small" onClick={() => setOpen(false)}>
                <CloseIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Stack>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {PRESET_TAGS.map((tag) => (
                <Chip
                  key={tag.value}
                  label={tag.label}
                  size="small"
                  variant={selectedTags.includes(tag.value) ? 'filled' : 'outlined'}
                  color={selectedTags.includes(tag.value) ? 'error' : 'default'}
                  onClick={() => handleToggleTag(tag.value)}
                  sx={{ fontSize: '0.7rem', height: 24 }}
                />
              ))}
            </Box>

            <TextField
              placeholder="補充說明（選填）"
              multiline
              minRows={1}
              maxRows={3}
              fullWidth
              size="small"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              sx={{ '& .MuiInputBase-input': { fontSize: '0.8rem' } }}
            />

            <Stack direction="row" justifyContent="flex-end">
              <Fab
                size="small"
                color="error"
                disabled={submitting || (selectedTags.length === 0 && !comment.trim())}
                onClick={handleSubmit}
              >
                <SendIcon sx={{ fontSize: 14 }} />
              </Fab>
            </Stack>
          </Stack>
        </Paper>
      </Fade>
    </Box>
  );
}
